// liquid-gl as a progressive enhancement for fixed glass bars. The element
// always carries the CSS `.glass` fallback; WebGL refraction is added only when
// motion is allowed, WebGL works and the user did not opt out (?glass=css).
type Lens = { destroy?: () => void };

export function glassEnhancementAllowed() {
  if (typeof window === 'undefined') return false;
  if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return false;
  if (new URLSearchParams(window.location.search).get('glass') === 'css') return false;
  if (/jsdom|HeadlessChrome/i.test(navigator.userAgent)) return false;
  try {
    const c = document.createElement('canvas');
    return !!(c.getContext('webgl2') || c.getContext('webgl'));
  } catch {
    return false;
  }
}

/** Attaches a lens to `selector`; returns a disposer. Never throws. */
export async function attachLiquidGlass(selector: string, opts: { zIndex: number }): Promise<() => void> {
  if (!glassEnhancementAllowed()) return () => {};
  try {
    const { default: liquidGL } = (await import('liquid-gl')) as unknown as {
      default: (o: Record<string, unknown>) => Lens | Lens[];
    };
    const lens = liquidGL({
      target: selector,
      snapshot: 'body',
      resolution: Math.min(2, window.devicePixelRatio || 1),
      zIndex: opts.zIndex,
      refraction: 0.012,
      bevelDepth: 0.06,
      bevelWidth: 0.2,
      frost: 2,
      shadow: false,
      specular: true,
      reveal: 'fade',
      tint: 'rgba(8, 10, 16, 0.35)',
    });
    const lenses = Array.isArray(lens) ? lens : [lens];
    return () => lenses.forEach((l) => l?.destroy?.());
  } catch {
    return () => {};
  }
}
